import { useRef, useState } from "react";

type Props = {
  label?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
};

const clamp = (v: number, a: number, b: number) => Math.min(Math.max(v, a), b);

/**
 * Blender-style numeric field: click the arrows to step, drag the value left/
 * right to scrub, or click it to type an exact number. The precise, resamplable
 * control the animator's "Integer" node uses.
 */
export function NumberField({ label, value, min, max, step, onChange, format }: Props) {
  const drag = useRef<{ x: number; v: number; moved: boolean } | null>(null);
  const [editing, setEditing] = useState(false);
  const decimals = (String(step).split(".")[1] || "").length;

  const snap = (v: number) => +clamp(Math.round(v / step) * step, min, max).toFixed(decimals);

  const onDown = (e: React.PointerEvent) => {
    if (editing) return;
    drag.current = { x: e.clientX, v: value, moved: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 2) drag.current.moved = true;
    onChange(snap(drag.current.v + dx * ((max - min) / 320)));
  };
  const onUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (d && !d.moved) setEditing(true); // a click (no drag) → type
  };

  return (
    <div className="numfield">
      {label && <span className="numfield-label">{label}</span>}
      <div className="numfield-box">
        <button className="numfield-arrow" tabIndex={-1} onClick={() => onChange(snap(value - step))}>‹</button>
        {editing ? (
          <input
            className="numfield-input"
            autoFocus
            defaultValue={String(value)}
            onBlur={(e) => { const n = parseFloat(e.target.value.replace(/,/g, "")); if (!Number.isNaN(n)) onChange(snap(n)); setEditing(false); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") setEditing(false);
            }}
          />
        ) : (
          <span className="numfield-value" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}>
            {format ? format(value) : value}
          </span>
        )}
        <button className="numfield-arrow" tabIndex={-1} onClick={() => onChange(snap(value + step))}>›</button>
      </div>
    </div>
  );
}
